import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import {
  AlertTriangle,
  Trash2,
  LogOut,
  XCircle,
  UserMinus,
  CheckCircle2,
  X,
  Loader2,
} from 'lucide-react';

export type ConfirmOptions = {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  icon?: 'trash' | 'logout' | 'alert' | 'x-circle' | 'user-minus';
};

export type SuccessOptions = {
  title: string;
  message?: string;
  buttonText?: string;
  autoCloseMs?: number;
};

type ConfirmDialogContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  showSuccess: (options: SuccessOptions) => Promise<void>;
};

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | null>(null);

export const useConfirmDialog = (): ConfirmDialogContextValue => {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirmDialog must be used within a ConfirmDialogProvider');
  }
  return ctx;
};

export const ConfirmDialogProvider = ({ children }: { children: ReactNode }) => {
  // Confirm Modal State
  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    options: ConfirmOptions;
    isLoading: boolean;
  }>({
    isOpen: false,
    options: {
      title: '',
      message: '',
    },
    isLoading: false,
  });

  const confirmResolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmState({
        isOpen: true,
        options,
        isLoading: false,
      });
    });
  }, []);

  const handleConfirmAction = () => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(true);
      confirmResolverRef.current = null;
    }
    setConfirmState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleCancelAction = () => {
    if (confirmResolverRef.current) {
      confirmResolverRef.current(false);
      confirmResolverRef.current = null;
    }
    setConfirmState((prev) => ({ ...prev, isOpen: false }));
  };

  // Success Modal State
  const [successState, setSuccessState] = useState<{
    isOpen: boolean;
    options: SuccessOptions;
  }>({
    isOpen: false,
    options: {
      title: '',
    },
  });

  const successResolverRef = useRef<(() => void) | null>(null);
  const autoCloseTimerRef = useRef<number | null>(null);

  const showSuccess = useCallback((options: SuccessOptions): Promise<void> => {
    return new Promise((resolve) => {
      successResolverRef.current = resolve;
      setSuccessState({
        isOpen: true,
        options,
      });

      if (autoCloseTimerRef.current) {
        window.clearTimeout(autoCloseTimerRef.current);
      }

      const closeDelay = options.autoCloseMs ?? 2200;
      if (closeDelay > 0) {
        autoCloseTimerRef.current = window.setTimeout(() => {
          handleCloseSuccess();
        }, closeDelay);
      }
    });
  }, []);

  const handleCloseSuccess = () => {
    if (autoCloseTimerRef.current) {
      window.clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
    if (successResolverRef.current) {
      successResolverRef.current();
      successResolverRef.current = null;
    }
    setSuccessState((prev) => ({ ...prev, isOpen: false }));
  };

  const { options: cOpts } = confirmState;
  const variant = cOpts.variant || 'danger';
  const iconType = cOpts.icon || (variant === 'danger' ? 'trash' : 'alert');

  return (
    <ConfirmDialogContext.Provider value={{ confirm, showSuccess }}>
      {children}

      {/* ================= WARNING / CONFIRM MODAL ================= */}
      {confirmState.isOpen && (
        <div
          className="fixed inset-0 z-[250] flex items-center justify-center bg-black/55 backdrop-blur-xs p-4 animate-in fade-in duration-200"
          onClick={handleCancelAction}
          role="dialog"
          aria-modal="true"
          aria-label={cOpts.title}
        >
          <div
            className="relative w-full max-w-[420px] rounded-[24px] bg-white p-6 sm:p-7 shadow-[0_20px_60px_rgba(0,0,0,0.25)] text-center transition-all duration-200 animate-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={handleCancelAction}
              className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>

            {/* Warning Icon Container */}
            <div className="mx-auto mb-4 flex h-[68px] w-[68px] items-center justify-center rounded-full bg-red-50 text-red-500 ring-8 ring-red-50/60">
              {iconType === 'trash' ? (
                <Trash2 size={32} className="text-red-500 animate-bounce" />
              ) : iconType === 'logout' ? (
                <LogOut size={32} className="text-[#FF6A1A]" />
              ) : iconType === 'x-circle' ? (
                <XCircle size={32} className="text-red-500" />
              ) : iconType === 'user-minus' ? (
                <UserMinus size={32} className="text-amber-500" />
              ) : (
                <AlertTriangle size={32} className="text-amber-500" />
              )}
            </div>

            {/* Title */}
            <h3 className="font-host text-[20px] font-bold text-[#1A1A1A] leading-snug">
              {cOpts.title}
            </h3>

            {/* Message */}
            <p className="mt-2.5 font-host text-[14px] text-[#666666] leading-relaxed">
              {cOpts.message}
            </p>

            {/* Action Buttons */}
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={handleCancelAction}
                disabled={confirmState.isLoading}
                className="flex-1 min-h-[46px] rounded-full border border-[#E0E0E0] bg-white font-host text-[14px] font-semibold text-[#555555] transition hover:bg-gray-50 active:scale-95 disabled:opacity-50 cursor-pointer"
              >
                {cOpts.cancelText || 'Cancel'}
              </button>

              <button
                type="button"
                onClick={handleConfirmAction}
                disabled={confirmState.isLoading}
                className={`flex-1 min-h-[46px] rounded-full font-host text-[14px] font-bold text-white transition hover:brightness-105 active:scale-95 shadow-md flex items-center justify-center gap-2 cursor-pointer ${
                  variant === 'warning'
                    ? 'bg-[#FF6A1A] hover:bg-[#E55A12]'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {confirmState.isLoading && <Loader2 size={16} className="animate-spin" />}
                {cOpts.confirmText || (variant === 'danger' ? 'Delete' : 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= SUCCESS POPUP MODAL ================= */}
      {successState.isOpen && (
        <div
          className="fixed inset-0 z-[260] flex items-center justify-center bg-black/45 backdrop-blur-xs p-4 animate-in fade-in duration-200"
          onClick={handleCloseSuccess}
          role="dialog"
          aria-modal="true"
          aria-label={successState.options.title}
        >
          <div
            className="relative w-full max-w-[380px] rounded-[24px] bg-white p-6 sm:p-7 shadow-[0_20px_60px_rgba(0,0,0,0.25)] text-center transition-all duration-200 animate-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Icon */}
            <div className="mx-auto mb-4 flex h-[68px] w-[68px] items-center justify-center rounded-full bg-[#EAF6D4] text-[#4CAF50] ring-8 ring-[#EAF6D4]/60">
              <CheckCircle2 size={36} className="text-[#4CAF50]" />
            </div>

            {/* Title */}
            <h3 className="font-host text-[20px] font-bold text-[#1A1A1A] leading-snug">
              {successState.options.title}
            </h3>

            {/* Message */}
            {successState.options.message && (
              <p className="mt-2 font-host text-[14px] text-[#666666] leading-relaxed">
                {successState.options.message}
              </p>
            )}

            {/* Close / Done Button */}
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={handleCloseSuccess}
                className="w-full min-h-[46px] rounded-full bg-[#1A1A1A] font-host text-[14px] font-bold text-white transition hover:bg-gray-800 active:scale-95 shadow-md cursor-pointer"
              >
                {successState.options.buttonText || 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmDialogContext.Provider>
  );
};
